/*
 * cluutch-slideshow.js
 * Comportement du carrousel horizontal (section « Cluutch Slideshow »).
 * Le défilement est assuré par scroll-snap (CSS) : ce script ne fait que piloter la position,
 * l'état des contrôles et le défilement automatique. Aucune dépendance.
 *
 * Choix structurants :
 * - défilement auto en setTimeout replanifié (et non setInterval) : pause et reprise exactes ;
 * - mise en pause au survol, au focus, quand l'onglet passe en arrière-plan et quand la section
 *   sort de l'écran (un seul IntersectionObserver, posé sur l'hôte) ;
 * - « inert » sur les slides hors écran : sans cela le focus clavier y entre et se bat avec le
 *   snap. Posé par le script uniquement, donc sans JS tout reste atteignable ;
 * - prefers-reduced-motion : pas de défilement auto, déplacements instantanés ;
 * - disconnectedCallback symétrique : chaque rechargement de section dans l'éditeur de thème
 *   libère minuteur, observateur et écouteurs (sinon la cadence s'accélère à chaque édition).
 * Auteur : Cluutch / Studio Hogo.
 */
(function () {
	if (customElements.get('cluutch-slideshow')) return;

	var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

	class CluutchSlideshow extends HTMLElement {
		constructor() {
			super();
			this.index = 0;
			this.timer = null;
			this.paused = false;
			this.rafPending = false;
			this.onScroll = this.onScroll.bind(this);
			this.onKeydown = this.onKeydown.bind(this);
			this.onVisibility = this.onVisibility.bind(this);
			this.pause = this.pause.bind(this);
			this.resume = this.resume.bind(this);
			this.onUserIntent = this.onUserIntent.bind(this);
		}

		connectedCallback() {
			this.track = this.querySelector('.cl-slideshow__track');
			if (!this.track) return;

			this.slides = Array.from(this.querySelectorAll('.cl-slideshow__slide'));
			this.dots = Array.from(this.querySelectorAll('.cl-slideshow__dots button'));
			this.arrows = Array.from(this.querySelectorAll('.cl-slideshow__arrow'));
			this.toggle = this.querySelector('.cl-slideshow__toggle');

			this.autoplayDelay = parseInt(this.dataset.autoplay || '0', 10);
			this.loop = this.dataset.loop === 'true';

			if (this.slides.length < 2) {
				this.syncInert();
				return;
			}

			this.track.addEventListener('scroll', this.onScroll, { passive: true });
			this.addEventListener('keydown', this.onKeydown);
			['pointerdown', 'focusin'].forEach((evt) => this.addEventListener(evt, this.onUserIntent));

			this.arrows.forEach((btn) => {
				btn.addEventListener('click', () => {
					this.goTo(this.index + (btn.dataset.dir === 'next' ? 1 : -1));
					this.restart();
				});
			});

			this.dots.forEach((btn, i) => {
				btn.addEventListener('click', () => {
					this.goTo(i);
					this.restart();
				});
			});

			if (this.toggle) {
				this.toggle.addEventListener('click', () => {
					this.paused ? this.resume(true) : this.pause(true);
				});
			}

			// Pause au survol et au focus : le contenu reste lisible pendant la lecture.
			this.addEventListener('mouseenter', this.pause);
			this.addEventListener('mouseleave', this.resume);
			this.addEventListener('focusin', this.pause);
			this.addEventListener('focusout', this.resume);
			document.addEventListener('visibilitychange', this.onVisibility);

			if ('IntersectionObserver' in window) {
				this.observer = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => (entry.isIntersecting ? this.resume() : this.pause()));
					},
					{ threshold: 0.2 }
				);
				this.observer.observe(this);
			}

			this.syncUI();
			this.start();

			// Aperçu dans l'éditeur de thème : afficher la slide sélectionnée.
			this.addEventListener('shopify:block:select', (e) => {
				var i = this.slides.indexOf(e.target);
				if (i > -1) this.goTo(i);
				this.pause();
			});
			this.addEventListener('shopify:block:deselect', this.resume);
		}

		disconnectedCallback() {
			this.stop();
			if (this.track) this.track.removeEventListener('scroll', this.onScroll);
			this.removeEventListener('keydown', this.onKeydown);
			['pointerdown', 'focusin'].forEach((evt) => this.removeEventListener(evt, this.onUserIntent));
			this.removeEventListener('mouseenter', this.pause);
			this.removeEventListener('mouseleave', this.resume);
			this.removeEventListener('focusin', this.pause);
			this.removeEventListener('focusout', this.resume);
			document.removeEventListener('visibilitychange', this.onVisibility);
			if (this.observer) this.observer.disconnect();
		}

		/* ----- position ----- */

		goTo(i) {
			var last = this.slides.length - 1;
			if (i > last) i = this.loop ? 0 : last;
			if (i < 0) i = this.loop ? last : 0;
			this.track.scrollTo({
				left: this.track.clientWidth * i,
				behavior: REDUCED.matches ? 'auto' : 'smooth'
			});
			this.index = i;
			this.syncUI();
		}

		onScroll() {
			if (this.rafPending) return;
			this.rafPending = true;
			requestAnimationFrame(() => {
				this.rafPending = false;
				var width = this.track.clientWidth;
				if (!width) return;
				var i = Math.round(this.track.scrollLeft / width);
				if (i !== this.index) {
					this.index = i;
					this.syncUI();
				}
			});
		}

		onKeydown(e) {
			var map = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
			if (!(e.key in map)) return;
			e.preventDefault();
			this.goTo(map[e.key]);
			this.restart();
		}

		/* ----- état visible ----- */

		syncUI() {
			this.dots.forEach((btn, i) => {
				var current = i === this.index;
				btn.setAttribute('aria-current', current ? 'true' : 'false');
			});
			if (!this.loop) {
				this.arrows.forEach((btn) => {
					var atEnd = btn.dataset.dir === 'next' ? this.index >= this.slides.length - 1 : this.index <= 0;
					btn.disabled = atEnd;
				});
			}
			this.syncInert();
		}

		// Les slides hors écran ne doivent pas capter le focus (piège classique du scroll-snap).
		syncInert() {
			if (!this.slides) return;
			this.slides.forEach((slide, i) => {
				if (i === this.index) slide.removeAttribute('inert');
				else slide.setAttribute('inert', '');
			});
		}

		/* ----- défilement automatique ----- */

		start() {
			if (!this.autoplayDelay || REDUCED.matches || this.slides.length < 2) return;
			this.stop();
			this.timer = window.setTimeout(() => {
				this.goTo(this.index + 1);
				this.start();
			}, this.autoplayDelay);
		}

		stop() {
			if (this.timer) {
				window.clearTimeout(this.timer);
				this.timer = null;
			}
		}

		restart() {
			if (this.paused) return;
			this.start();
		}

		// explicit : action volontaire sur le bouton pause/lecture (mémorisée).
		pause(explicit) {
			this.stop();
			if (explicit === true) {
				this.paused = true;
				if (this.toggle) this.toggle.setAttribute('aria-pressed', 'true');
			}
		}

		resume(explicit) {
			if (explicit === true) {
				this.paused = false;
				if (this.toggle) this.toggle.setAttribute('aria-pressed', 'false');
			}
			if (this.paused || document.hidden) return;
			this.start();
		}

		onVisibility() {
			document.hidden ? this.stop() : this.resume();
		}

		// Toute intention utilisateur repousse le passage automatique à la slide suivante.
		onUserIntent() {
			this.restart();
		}
	}

	customElements.define('cluutch-slideshow', CluutchSlideshow);
})();
